import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Field, Icon, Input, Text, VStack, Center, Image } from '@chakra-ui/react';
import { HiOutlineEnvelope, HiOutlineShieldCheck } from 'react-icons/hi2';
import PasswordInput from '@/components/ui/foundation/PasswordInput';
import Logo from '@/assets/images/cyberjob_dialogflow_logo.svg';
import { useAuth } from '@/auth/AuthContext';
import useResendCooldown from '@/auth/useResendCooldown';
import AccountService from '@/services/AccountService';

const LoginPage = () => {
    const { t } = useTranslation('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login, completeTwoFactorLogin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [step, setStep] = useState('credentials');
    const [pendingToken, setPendingToken] = useState('');
    const [twoFactorMethod, setTwoFactorMethod] = useState('Totp');
    const [twoFactorInfo, setTwoFactorInfo] = useState('');
    const [isResending, setIsResending] = useState(false);
    const resendCooldown = useResendCooldown();
    const isEmailMethod = twoFactorMethod === 'Email';
    const [code, setCode] = useState('');
    const [twoFactorError, setTwoFactorError] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);

    const redirectTo = location.state?.from?.pathname ?? '/models';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const result = await login(email, password);
            if (result?.success) {
                navigate(redirectTo, { replace: true });
            } else if (result?.requiresTwoFactor) {
                setPendingToken(result.pendingToken);
                setTwoFactorMethod(result.twoFactorMethod);
                if (result.twoFactorMethod === 'Email') resendCooldown.start();
                setStep('twoFactor');
            } else {
                setError(result?.error || t('loginPage.errorInvalidCredentials'));
            }
        } catch (err) {
            setError(err?.response?.status === 401
                ? t('loginPage.errorInvalidCredentials')
                : t('loginPage.errorGeneric'));
        } finally {
            setIsLoading(false);
        }
    };

    const invalidCodeMessage = isEmailMethod
        ? t('loginPage.twoFactor.errorInvalidEmailCode')
        : t('loginPage.twoFactor.errorInvalidCode');

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setTwoFactorError('');
        setTwoFactorInfo('');
        setIsVerifying(true);
        try {
            const result = await completeTwoFactorLogin(pendingToken, code);
            if (result?.success) {
                navigate(redirectTo, { replace: true });
            } else {
                setTwoFactorError(invalidCodeMessage);
            }
        } catch {
            setTwoFactorError(invalidCodeMessage);
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendCode = async () => {
        setTwoFactorError('');
        setTwoFactorInfo('');
        setIsResending(true);
        try {
            await AccountService.resendTwoFactorLoginCode(pendingToken);
            setTwoFactorInfo(t('loginPage.twoFactor.codeResent'));
            resendCooldown.start();
        } catch (err) {
            if (err?.response?.status === 429) {
                resendCooldown.start();
            } else {
                setTwoFactorError(t('loginPage.twoFactor.errorSessionExpired'));
            }
        } finally {
            setIsResending(false);
        }
    };

    const handleBackToCredentials = () => {
        setStep('credentials');
        setCode('');
        setTwoFactorError('');
        setTwoFactorInfo('');
        setPendingToken('');
        setTwoFactorMethod('Totp');
        resendCooldown.reset();
    };

    if (step === 'twoFactor') {
        const TwoFactorIcon = isEmailMethod ? HiOutlineEnvelope : HiOutlineShieldCheck;
        return (
            <Center minH="100vh" w="full" bg="bg.primary" px={4} py={8}>
                <Box w="full" maxW="420px" p={{ base: 6, md: 10 }} bg="bg.secondary" border="1px solid" borderColor="border.primary" borderRadius={20} boxShadow="0 8px 32px rgba(23,21,31,0.10)">
                    <VStack gap={6} as="form" onSubmit={handleVerifyCode} align="center">
                        <Image src={Logo} w="55%" />
                        <Center w={14} h={14} borderRadius="full" bg="bg.dark" color="gray.900">
                            <Icon as={TwoFactorIcon} boxSize={7} />
                        </Center>
                        <VStack gap={2}>
                            <Text fontSize={24} color="gray.900" fontWeight="bold" textAlign="center">{t('loginPage.twoFactor.title')}</Text>
                            <Text color="text.grey" fontSize={14} textAlign="center" lineHeight="1.6" maxW="320px">
                                {isEmailMethod ? t('loginPage.twoFactor.messageEmail') : t('loginPage.twoFactor.message')}
                            </Text>
                        </VStack>

                        <Field.Root required w="full">
                            <Field.Label color="gray.900" justifyContent="center" w="full">{t('loginPage.twoFactor.codeLabel')}</Field.Label>
                            <Input
                                variant="primary"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                autoFocus
                                maxLength={6}
                                placeholder={t('loginPage.twoFactor.codePlaceholder')}
                                value={code}
                                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                textAlign="center"
                                fontSize={28}
                                fontWeight="semibold"
                                letterSpacing="0.5em"
                                h={14}
                                pl="0.5em"
                            />
                        </Field.Root>

                        {twoFactorError && (
                            <Text color="red.400" fontSize={14} w="full" textAlign="center">{twoFactorError}</Text>
                        )}
                        {twoFactorInfo && !twoFactorError && (
                            <Text color="green.600" fontSize={14} w="full" textAlign="center">{twoFactorInfo}</Text>
                        )}

                        <VStack gap={3} w="full">
                            <Button variant="primary" type="submit" w="full" loading={isVerifying} disabled={code.length < 6}>
                                {t('loginPage.twoFactor.submit')}
                            </Button>

                            {isEmailMethod && (
                                <Button
                                    variant="outlined"
                                    w="full"
                                    type="button"
                                    onClick={handleResendCode}
                                    loading={isResending}
                                    disabled={resendCooldown.isCoolingDown}
                                >
                                    {resendCooldown.isCoolingDown
                                        ? t('loginPage.twoFactor.resendCooldown', { seconds: resendCooldown.secondsLeft })
                                        : t('loginPage.twoFactor.resendCode')}
                                </Button>
                            )}
                        </VStack>

                        <VStack gap={2} pt={2} borderTop="1px solid" borderColor="border.primary" w="full">
                            <Text
                                as="button"
                                type="button"
                                color="text.grey"
                                fontSize={14}
                                cursor="pointer"
                                pt={4}
                                _hover={{ color: 'gray.900' }}
                                onClick={handleBackToCredentials}
                            >
                                {t('loginPage.twoFactor.backToLogin')}
                            </Text>
                            <Link to="/2fa-recovery">
                                <Text color="text.grey" fontSize={13} textDecoration="underline" _hover={{ color: 'gray.900' }}>
                                    {isEmailMethod ? t('loginPage.twoFactor.noAccessEmail') : t('loginPage.twoFactor.noAccess')}
                                </Text>
                            </Link>
                        </VStack>
                    </VStack>
                </Box>
            </Center>
        );
    }

    return (
        <Center minH="100vh" w="100vw" bg="bg.primary" px={{ base: 4, md: 0 }}>
            <Box w="full" maxW="400px" p={{ base: 6, md: 8 }} bg="bg.secondary" border="1px solid" borderColor="border.primary" borderRadius={16} boxShadow="0 4px 24px rgba(23,21,31,0.12)">
                <VStack gap={6} as="form" onSubmit={handleSubmit}>
                    <Image src={Logo} w="70%" mb={2} />
                    <Text fontSize={24} color="gray.900" fontWeight="bold">{t('loginPage.title')}</Text>

                    <Field.Root required w="full">
                        <Field.Label color="gray.900">{t('loginPage.emailLabel')} <Field.RequiredIndicator /></Field.Label>
                        <Input
                            variant="primary"
                            type="email"
                            placeholder={t('loginPage.emailPlaceholder')}
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </Field.Root>

                    <Field.Root required w="full">
                        <Field.Label color="gray.900">{t('loginPage.passwordLabel')} <Field.RequiredIndicator /></Field.Label>
                        <PasswordInput value={password} onChange={e => setPassword(e.target.value)} />
                    </Field.Root>

                    {error && (
                        <Text color="red.400" fontSize={14} w="full">{error}</Text>
                    )}

                    <Button variant="primary" type="submit" w="full" loading={isLoading}>
                        {t('loginPage.submit')}
                    </Button>

                    <Link to="/forgot-password">
                        <Text color="text.grey" fontSize={14} _hover={{ color: 'gray.900' }}>
                            {t('loginPage.forgotPassword')}
                        </Text>
                    </Link>
                </VStack>
            </Box>
        </Center>
    );
};

export default LoginPage;
